import { Button, Skeleton } from "@resume-studio/ui";
import { ArrowRight, Megaphone, Users } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";

import { useAdminFeedbackKpis, useAdminOverview } from "@/admin/services";

import { Card } from "../../components/ui/card";
import { EmptyState } from "../_components/empty-state";
import { KPICard } from "../_components/kpi-card";
import { PageHeader } from "../_components/page-header";

const formatRelative = (iso: string) => {
  const date = new Date(iso);
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  return date.toLocaleDateString();
};

const PanelLink = ({ to, label }: { to: string; label: string }) => (
  <Link
    to={to}
    className="inline-flex items-center gap-1 rounded-sm px-2 py-1 text-[12px] font-medium text-primary transition-colors duration-120 ease-out hover:bg-primary-soft"
  >
    {label}
    <ArrowRight className="size-3" />
  </Link>
);

export const AdminOverviewPage = () => {
  const { data, isLoading } = useAdminOverview();
  const { data: feedbackKpis, isLoading: feedbackKpisLoading } = useAdminFeedbackKpis();

  return (
    <>
      <Helmet>
        <title>Overview · Admin</title>
      </Helmet>

      <PageHeader
        title="Overview"
        description="The 'should I be worried right now' page. Refreshes every minute."
        trail={["Admin"]}
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {isLoading
          ? Array.from({ length: 4 }).map((_, i) => (
              <KPICard key={i} label="Loading" value="—" loading />
            ))
          : (data?.kpis ?? []).map((kpi) => (
              <KPICard
                key={kpi.label}
                label={kpi.label}
                value={kpi.value.toLocaleString()}
                wowDelta={kpi.wowDelta}
              />
            ))}
      </div>

      {/*
        Voice-of-customer strip. Surfaces inbox workload and actionable churn
        signals; score analytics live in PostHog.
      */}
      <section className="mt-8">
        <div className="mb-3 flex items-baseline justify-between">
          <div>
            <h2 className="text-[14px] font-semibold text-foreground">Voice of customer</h2>
            <p className="text-[12px] text-muted-foreground">
              Actionable feedback rows from surveys and the always-on widget.
            </p>
          </div>
          <PanelLink to="/feedback" label="Open inbox" />
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4">
          {feedbackKpisLoading || !feedbackKpis ? (
            Array.from({ length: 4 }).map((_, i) => (
              <KPICard key={i} label="Loading" value="—" loading />
            ))
          ) : (
            <>
              <KPICard
                label="New (untriaged)"
                value={feedbackKpis.newCount.toLocaleString()}
                hint="Untouched feedback rows"
              />
              <KPICard
                label="Detractor follow-ups · 30d"
                value={feedbackKpis.detractorFollowupCount30d.toLocaleString()}
                hint="NPS comments only"
              />
              <KPICard
                label="Exit feedback · 30d"
                value={feedbackKpis.exitSurveyCount30d.toLocaleString()}
                hint="Pre-portal reasons"
              />
              <KPICard
                label="> 48h unanswered"
                value={feedbackKpis.unansweredOlderThan48hCount.toLocaleString()}
                hint="NEW or READ"
              />
            </>
          )}
        </div>
      </section>

      <section className="mt-8 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card
          title="Active campaigns"
          description="Live offers and their pacing."
          actions={<PanelLink to="/campaigns" label="Manage" />}
          unpadded
        >
          <div className="px-5 py-4">
            {isLoading ? (
              <Skeleton className="h-24 w-full" />
            ) : (data?.activeCampaigns?.length ?? 0) === 0 ? (
              <EmptyState
                icon={Megaphone}
                title="No active campaigns"
                description="Launch a discount or free-access campaign from the Campaigns tab."
                action={
                  <Button size="sm" asChild>
                    <Link to="/campaigns/new">New campaign</Link>
                  </Button>
                }
              />
            ) : (
              <ul className="divide-y divide-border/60">
                {data?.activeCampaigns.map((c) => (
                  <li
                    key={c.slug}
                    className="-mx-2 flex items-center justify-between rounded-sm px-2 py-2.5 transition-colors duration-120 ease-out hover:bg-surface"
                  >
                    <div className="min-w-0">
                      <Link
                        to={`/campaigns?slug=${encodeURIComponent(c.slug)}`}
                        className="block truncate text-[13px] font-medium text-foreground hover:underline"
                      >
                        {c.name}
                      </Link>
                      <p className="text-[12px] text-muted-foreground">
                        {c.offerType === "STRIPE_DISCOUNT" ? "Stripe discount" : "Free access"}
                        {" · ends "}
                        <span className="tabular-nums">
                          {new Date(c.endsAt).toLocaleDateString()}
                        </span>
                      </p>
                    </div>
                    <div className="text-right text-[12px] tabular-nums">
                      <div className="font-medium text-foreground">
                        {c.redemptions.toLocaleString()}
                      </div>
                      <div className="text-subtle">
                        {c.maxRedemptions ? `of ${c.maxRedemptions}` : "uncapped"}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>

        <Card
          title="Recent signups"
          description="Newest 10 accounts."
          actions={<PanelLink to="/users" label="Search" />}
          unpadded
        >
          <div className="px-5 py-4">
            {isLoading ? (
              <Skeleton className="h-24 w-full" />
            ) : (data?.recentSignups?.length ?? 0) === 0 ? (
              <EmptyState
                icon={Users}
                title="No signups yet"
                description="When users register, they'll show up here."
              />
            ) : (
              <ul className="divide-y divide-border/60">
                {data?.recentSignups.map((u) => (
                  <li
                    key={u.id}
                    className="-mx-2 flex items-center justify-between rounded-sm px-2 py-2.5 transition-colors duration-120 ease-out hover:bg-surface"
                  >
                    <div className="min-w-0">
                      <Link
                        to={`/users/${u.id}`}
                        className="block truncate text-[13px] font-medium text-foreground hover:underline"
                      >
                        {u.name}
                      </Link>
                      <p className="truncate text-[12px] text-muted-foreground">{u.email}</p>
                    </div>
                    <span className="shrink-0 text-[12px] tabular-nums text-subtle">
                      {formatRelative(u.createdAt)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>
      </section>
    </>
  );
};

export default AdminOverviewPage;
